import * as React from 'react';
import { FileUp, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import type { PublishScheduleDataScheduleEntriesInput } from '@/lib/gql/__generated__/graphql';

interface SchedulePublisherProps {
  onPublish: (input: {
    sourceDocumentName: string;
    supportedRoutes: Array<string>;
    scheduleEntries: Array<PublishScheduleDataScheduleEntriesInput>;
  }) => void;
  busy: boolean;
}

const isEntry = (value: unknown): value is PublishScheduleDataScheduleEntriesInput => {
  if (!value || typeof value !== 'object') return false;
  const record = value as Record<string, unknown>;
  return [
    'route',
    'direction',
    'stopId',
    'stopName',
    'serviceDay',
    'tripId',
    'scheduledArrival',
    'destinationLabel',
  ].every((key) => typeof record[key] === 'string' && record[key] !== '');
};

export function SchedulePublisher({ onPublish, busy }: SchedulePublisherProps) {
  const [source, setSource] = React.useState('');
  const [routes, setRoutes] = React.useState('');
  const [entries, setEntries] = React.useState('');
  const [message, setMessage] = React.useState('');
  const submit = () => {
    let scheduleEntries: Array<PublishScheduleDataScheduleEntriesInput> = [];
    try {
      const parsed: unknown = JSON.parse(entries);
      if (Array.isArray(parsed)) scheduleEntries = parsed.filter(isEntry);
    } catch {
      // The mutation owns validation; an incomplete draft must still reach it.
    }
    onPublish({
      sourceDocumentName: source.trim(),
      supportedRoutes: routes
        .split(',')
        .map((route) => route.trim())
        .filter(Boolean),
      scheduleEntries,
    });
    setMessage('');
  };
  return (
    <Card className="border-primary/20 bg-card/90">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileUp className="size-5 text-primary" /> Publish schedule data
        </CardTitle>
        <CardDescription>
          Document the source behind these scheduled departures. Use one JSON object per entry in the array.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="source-document">Source document name</Label>
            <Input
              id="source-document"
              data-jr-key="publish-source-document"
              value={source}
              onChange={(event) => setSource(event.target.value)}
              placeholder="King County Metro weekday schedule"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="supported-routes">Supported routes</Label>
            <Input
              id="supported-routes"
              data-jr-key="publish-supported-routes"
              value={routes}
              onChange={(event) => setRoutes(event.target.value)}
              placeholder="RapidRide E Line, Route 44"
            />
          </div>
        </div>
        <div className="space-y-2">
          <Label htmlFor="schedule-entries">Schedule entries (JSON array)</Label>
          <Textarea
            id="schedule-entries"
            data-jr-key="publish-schedule-entries"
            value={entries}
            onChange={(event) => setEntries(event.target.value)}
            className="min-h-32 font-mono text-xs"
            placeholder={
              '[{"route":"E Line","direction":"northbound","stopId":"...","stopName":"...","serviceDay":"2026-09-18","tripId":"...","scheduledArrival":"2026-09-18T08:15:00-07:00","destinationLabel":"..."}]'
            }
          />
        </div>
        {message && (
          <p className="text-sm text-destructive" role="alert">
            {message}
          </p>
        )}
        <Button data-jr-key="publish-schedule" onClick={submit} disabled={busy}>
          <Send className="size-4" /> {busy ? 'Publishing…' : 'Publish schedule data'}
        </Button>
      </CardContent>
    </Card>
  );
}
